import React from 'react'
import { Link } from '@inertiajs/react'
import { ArrowRight, CalendarClock } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { DeltaChip, percentFrom } from '@/components/dashboard/Delta'
import { cn } from '@/lib/utils'
import { formatMoney } from '@/lib/format'

/**
 * Band 3 — pace. Six months of revenue, three ways: what is on the books now,
 * what was on the books at the same point last year (STLY), and what those months
 * ultimately FINISHED at last year (LY final).
 *
 * LY final is the decision-driving series. A big green "+66% vs STLY" on a month
 * sitting at a third of last year's finish is not good news, it is runway — so the
 * gap to LY final gets the callout and the bolder tile treatment, and a month with
 * no STLY history draws nothing rather than a misleading zero-height bar.
 */

export interface PaceMonth {
  month: string
  monthLabel: string
  monthShort: string
  year: number
  otb: number
  stly: number
  lyEnd: number
  stlyDeltaPct: number | null
  lyEndDeltaPct: number | null
  occupancyOtbPct: number
  occupancyStlyPct: number
  occupancyDeltaPct: number | null
  performanceTier?: string | null
  hasStlyData: boolean
  hasLyEndData?: boolean
}

export interface PaceStlyMeta {
  monthsAvailable: number
  monthsTotal: number
  trackingStartedAt: string | null
  unlocksOn: string | null
}

export interface PaceData {
  months: PaceMonth[]
  stlyMeta: PaceStlyMeta | null
  asOfDate: string | null
  stlyAnchor: string | null
  currency: string
}

const SERIES = {
  otb: { label: 'On the books', color: '#0d9488' },
  stly: { label: 'STLY', color: '#cbd5e1' },
  lyEnd: { label: 'Last year final', color: '#334155' },
} as const

function formatDay(iso: string | null): string {
  if (!iso) return '—'
  const parsed = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** `hasLyEndData` predates nothing — fall back to the value itself when absent. */
function hasLyEnd(band: PaceMonth): boolean {
  return band.hasLyEndData ?? band.lyEnd > 0
}

interface ChartRow {
  key: string
  label: string
  monthShort: string
  year: number
  otb: number
  stly: number | null
  lyEnd: number | null
  band: PaceMonth
}

function PaceTooltip({ active, payload, currency }: any) {
  if (!active || !payload?.length) return null
  const row: ChartRow = payload[0].payload
  const band = row.band

  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2.5 shadow-lg shadow-black/5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {band.monthLabel} {band.year}
      </p>
      <dl className="mt-1.5 space-y-1">
        <TooltipRow color={SERIES.otb.color} label={SERIES.otb.label} value={formatMoney(band.otb, currency)} />
        <TooltipRow
          color={SERIES.stly.color}
          label={SERIES.stly.label}
          value={band.hasStlyData ? formatMoney(band.stly, currency) : 'No history yet'}
          muted={!band.hasStlyData}
        />
        <TooltipRow
          color={SERIES.lyEnd.color}
          label={SERIES.lyEnd.label}
          value={hasLyEnd(band) ? formatMoney(band.lyEnd, currency) : 'No history yet'}
          muted={!hasLyEnd(band)}
        />
      </dl>
      <p className="mt-2 border-t border-border/40 pt-1.5 text-[11px] text-muted-foreground tabular-nums">
        Occupancy {band.occupancyOtbPct}%
        {band.hasStlyData && <> · STLY {band.occupancyStlyPct}%</>}
      </p>
    </div>
  )
}

function TooltipRow({
  color,
  label,
  value,
  muted,
}: {
  color: string
  label: string
  value: string
  muted?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-6 text-xs">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: color }} />
        {label}
      </span>
      <span
        className={cn('font-semibold tabular-nums', muted ? 'italic text-muted-foreground' : 'text-foreground')}
      >
        {value}
      </span>
    </div>
  )
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="h-2 w-4 rounded-sm" style={{ backgroundColor: color }} />
      {label}
    </span>
  )
}

/** The month with the most last-year revenue still to be won back. */
function widestLyGap(months: PaceMonth[]): PaceMonth | null {
  const candidates = months.filter((band) => hasLyEnd(band) && band.lyEnd > band.otb)
  if (candidates.length === 0) return null
  return candidates.reduce((widest, band) =>
    band.lyEnd - band.otb > widest.lyEnd - widest.otb ? band : widest
  )
}

export default function PaceBand({ pace, propertyBasePath }: { pace: PaceData; propertyBasePath: string }) {
  const cur = pace.currency
  const months = pace.months
  const rows: ChartRow[] = months.map((band) => ({
    key: band.month,
    label: band.monthShort,
    monthShort: band.monthShort,
    year: band.year,
    otb: band.otb,
    // A month with no year-ago history draws NO bar. A zero-height bar would read
    // as "last year we sold nothing", which is a different — and false — claim.
    stly: band.hasStlyData ? band.stly : null,
    lyEnd: hasLyEnd(band) ? band.lyEnd : null,
    band,
  }))

  const gapMonth = widestLyGap(months)
  const noStly = pace.stlyMeta && pace.stlyMeta.monthsAvailable === 0

  return (
    <DashboardCard
      title="Pace — next 6 months"
      description={
        <>
          Revenue on the books against the same point last year and last year's final result
          {pace.stlyAnchor ? <> · STLY anchored on {formatDay(pace.stlyAnchor)}</> : null}
        </>
      }
      icon={<CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          "STLY is what was on the books at this point a year ago — it says whether you are ahead of your own booking curve. Last-year-final is what the month actually earned once it was over: that is the number still to be beaten, and early months always sit far below it.",
      }}
      action={
        <Link
          href={`${propertyBasePath}/reports/snapshot`}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary transition-colors hover:text-primary/80"
        >
          All 12 months
          <ArrowRight className="h-3 w-3" />
        </Link>
      }
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <Swatch color={SERIES.otb.color} label={SERIES.otb.label} />
        <Swatch color={SERIES.stly.color} label={SERIES.stly.label} />
        <Swatch color={SERIES.lyEnd.color} label={SERIES.lyEnd.label} />
      </div>

      <div className="mt-3">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={rows} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              width={78}
              tickFormatter={(value: number) => formatMoney(value, cur)}
            />
            <Tooltip cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }} content={<PaceTooltip currency={cur} />} />
            {/* Bars are drawn, not animated in: this is a decision surface, and a grow-from-zero
                animation that is throttled (background tab, reduced-motion, slow paint) leaves the
                chart looking empty. Same call TargetOutlookCard's sparkline makes. */}
            <Bar
              dataKey="otb"
              fill={SERIES.otb.color}
              radius={[4, 4, 0, 0]}
              maxBarSize={22}
              isAnimationActive={false}
            />
            <Bar
              dataKey="stly"
              fill={SERIES.stly.color}
              radius={[4, 4, 0, 0]}
              maxBarSize={22}
              isAnimationActive={false}
            />
            <Bar
              dataKey="lyEnd"
              fill={SERIES.lyEnd.color}
              radius={[4, 4, 0, 0]}
              maxBarSize={22}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {gapMonth && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Most last-year revenue still to win back
          </p>
          <p className="mt-1 text-sm leading-relaxed text-foreground">
            <span className="font-semibold">
              {gapMonth.monthLabel} {gapMonth.year}
            </span>{' '}
            finished at{' '}
            <span className="font-semibold tabular-nums">{formatMoney(gapMonth.lyEnd, cur)}</span> last
            year and has{' '}
            <span className="font-semibold tabular-nums">{formatMoney(gapMonth.otb, cur)}</span> on the
            books today.
          </p>
        </div>
      )}

      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-border/40 pt-4 sm:grid-cols-6">
        {months.map((band) => {
          const stlyDelta = band.hasStlyData ? percentFrom(band.stlyDeltaPct) : null
          const lyDelta = hasLyEnd(band) ? percentFrom(band.lyEndDeltaPct) : null
          return (
            <div key={band.month} className="min-w-0 rounded-lg bg-muted/40 px-2.5 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
                {band.monthShort}
                <span className="ml-1 font-normal text-muted-foreground">{band.year}</span>
              </p>
              <div className="mt-1.5 space-y-1">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">vs STLY</p>
                  {stlyDelta ? (
                    <DeltaChip delta={stlyDelta} className="mt-0.5" />
                  ) : (
                    <p className="mt-0.5 text-[11px] italic text-muted-foreground">No STLY</p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">vs LY final</p>
                  {lyDelta ? (
                    <DeltaChip delta={lyDelta} className="mt-0.5" />
                  ) : (
                    <p className="mt-0.5 text-[11px] italic text-muted-foreground">No LY final</p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {noStly && (
        <p className="mt-3 rounded-lg border border-dashed border-border px-4 py-3 text-[11px] leading-relaxed text-muted-foreground">
          No STLY comparison yet for any of these months
          {pace.stlyMeta?.trackingStartedAt && (
            <> — booking history starts {formatDay(pace.stlyMeta.trackingStartedAt)}</>
          )}
          {pace.stlyMeta?.unlocksOn && <>, so year-on-year unlocks on {formatDay(pace.stlyMeta.unlocksOn)}</>}
          .
        </p>
      )}
    </DashboardCard>
  )
}
